import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import { createCMS, getCMSByID, updateCMS } from "./services/cms.services";
import { clearCMSDetails } from "./cmsSlice";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import CMSImageArray from "./CMSImageArray";

const CreateCMS: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CMSID = location.state?.CMSID;

  const CMSDetail = useAppSelector((state) => state.cms.CMSDetail);
  const loading = useAppSelector((state) => state.cms.loading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CMSDetail]);

  useEffect(() => {
    if (CMSID) {
      dispatch(getCMSByID(CMSID));
    }
    return () => {
      dispatch(clearCMSDetails());
    };
  }, [CMSID]);

  const handleSubmit = async (values: any) => {
    try {
      if (CMSID) {
        const editPayload = {
          ID: CMSID,
          Name: values.Name,
          CMSURL: values.CMSURL,
          Template: values.Template,
          MetaDescription: values.MetaDescription,
          MetaKeyword: values.MetaKeyword,
          MetaTitle: values.MetaTitle,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(updateCMS(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          Name: values.Name,
          CMSURL: values.CMSURL,
          Template: values.Template,
          MetaDescription: values.MetaDescription,
          MetaKeyword: values.MetaKeyword,
          MetaTitle: values.MetaTitle,
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createCMS(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/cms");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={CMSDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4>
                      {CMSID ? "Update CMS" : "Create CMS"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem style={{ width: "100%" }}>
                    <Field
                      name="Name"
                      label="Name"
                      placeholder="Name"
                      validator={requiredValidator}
                      astrike={true}
                      component={FormTextField}
                      // options={CMSName?.map((status: any) => {
                      //   return {
                      //     value: status?.value,
                      //     label: status?.label,
                      //   };
                      // })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2} style={{ width: "100%" }}>
                    <Field
                      name="CMSURL"
                      label="URL"
                      placeholder="URL"
                      astrike={true}
                      component={FormTextField}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem style={{ width: "100%" }}>
                    <Field
                      id={"IsActive"}
                      name={"IsActive"}
                      label={"Status"}
                      astrike={true}
                      marginTop={20}
                      component={FormSelectionField}
                      validator={requiredValidator}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} style={{ width: "100%" }}>
                    <Field
                      name="MetaKeyword"
                      label="Meta Keywords"
                      placeholder="Meta Keywords"
                      // astrike={true}
                      component={FormTextField}
                      // validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      width: "100%",
                      display: "flex",
                      gap: "27px",
                      flexDirection: "column",
                    }}
                  >
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      // astrike={true}
                      component={FormTextField}
                      // validator={requiredValidator}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={4}
                    style={{ width: "100%", display: "flex", gap: 10 }}
                  >
                    <Field
                      wrapperStyle={{ width: "30%" }}
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta Description"
                      style={{ height: 300 }}
                      component={FormTextArea}
                    />
                    {/* </GridLayoutItem>
                  <GridLayoutItem colSpan={2} style={{ width: "100%" }}> */}
                    <Field
                      wrapperStyle={{ width: "70%" }}
                      name="Template"
                      label="Template"
                      placeholder="Template"
                      rows={8}
                      component={FormRichTextField}
                      minHeight={300}
                    />
                  </GridLayoutItem>
                  {/* <GridLayoutItem style={{ width: "100%" }}>
              
                  </GridLayoutItem> */}
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={CMSID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />

                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/cms")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
              {CMSID && (
                <ShadowCard style={{ marginTop: 10, marginBottom: 40 }}>
                  <GridLayout
                    style={{ marginTop: 30 }}
                    gap={{ rows: 5, cols: 10 }}
                  >
                    <GridLayoutItem>
                      <FieldArray
                        formRenderProps={formRenderProps}
                        component={CMSImageArray}
                        name="Images"
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </ShadowCard>
              )}
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateCMS;
